import { Table } from '@interstate/components/Table';
import { Typography } from '@interstate/components/Typography';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import { amountDueAtSigningPaidByContainerStyle } from '../dealSummary/common/ComponentLabelStyle';
import { NET_TRADE_IN_EQUITY } from '../constants';
import { createDealRootSelector } from '../offerCompareColumn/hooks/createDealRootSelector';
import * as dueAtSigningSelectors from '@makemydeal/dr-dash-store/selectors/dueAtSigningSelectors';
import type { DealHistory } from '@makemydeal/dr-dash-types';

type TableData = {
    extraData: string;
    id: number;
    key: number;
    item: string;
    amount: string;
};
type DueAtSigningProps = {
    deal?: DealHistory;
};

const AmountDueAtSigningPaidByTable: FC<DueAtSigningProps> = ({ deal }) => {
    const useDealRootSelector = createDealRootSelector(deal, 'AmountDueAtSigningPaidByTable');
    const payment = useDealRootSelector(dueAtSigningSelectors.getDasDefaultPayment, 'dasDefaultPayment');

    const netTradeValue = payment?.netTrade ?? 0;
    const netTradeValueOrZero = netTradeValue > 0 ? netTradeValue : 0;

    const totalRebates = payment?.totalRebates;
    const getCashDueFromCustomer = payment?.cashDueFromCustomer;
    const priorLoanBalance = payment?.priorLoanBalance;
    const totalAmountDue = payment?.totalAmountDue;

    const columns = [
        {
            className: 'item',
            dataIndex: 'item',
            title: 'Item',
            width: 'auto',
            render: (text: string, _record: TableData) => <span style={{ color: '#6D6D6D' }}>{text}</span>
        },
        {
            align: 'right' as const,
            className: 'amount',
            dataIndex: 'amount',
            title: 'Amount',
            width: 'auto'
        }
    ];

    const tableData: TableData[] = useMemo(
        () => [
            {
                extraData: 'Column not to be shown',
                id: 0,
                key: 0,
                item: NET_TRADE_IN_EQUITY,
                amount: formatDollarsAndCents(netTradeValueOrZero)
            },
            {
                extraData: 'Column not to be shown',
                id: 1,
                key: 1,
                item: 'Rebates and Noncash Credits',
                amount: formatDollarsAndCents(totalRebates)
            },
            {
                extraData: 'Column not to be shown',
                id: 2,
                key: 2,
                item: 'Amount Paid by Customer',
                amount: formatDollarsAndCents(getCashDueFromCustomer)
            },
            {
                extraData: 'Column not to be shown',
                id: 3,
                key: 3,
                item: 'Additional Capitalized Cost',
                amount: formatDollarsAndCents(priorLoanBalance)
            },
            {
                extraData: 'Column not to be shown',
                id: 4,
                key: 4,
                item: 'Amount Paid by Dealer',
                amount: '$0.00'
            },
            {
                extraData: 'Column not to be shown',
                id: 5,
                key: 5,
                item: 'Total',
                amount: formatDollarsAndCents(totalAmountDue)
            }
        ],
        [netTradeValue, totalRebates, getCashDueFromCustomer, priorLoanBalance, totalAmountDue]
    );

    return (
        <div style={amountDueAtSigningPaidByContainerStyle} data-testid="amount-due-at-signing-paid-by-container">
            <Typography tag="span" variant="h4" data-testid="amount-due-at-signing-paid-by-title">
                Amount Due at Signing Paid By
            </Typography>
            <Table
                columns={columns}
                data={tableData}
                id="TableRowClick"
                data-testid="amount-due-at-signing-paid-by-table"
                tableLayout="fixed"
            />
        </div>
    );
};

export default AmountDueAtSigningPaidByTable;
